/**
 * Supabase Realtime Hook
 * Issue #237: リアルタイム更新機能実装
 * 
 * 顧客・予約・施術データのリアルタイム同期を提供
 */

import { useEffect, useCallback, useRef } from 'react';
import logger from '../utils/logger';
import { getSupabase } from '../lib/supabase';
import type { Customer } from '../services/dataService';
import type { ExtendedReservation } from '../types/salonboard';

// リアルタイム更新のイベントタイプ
export type RealtimeEventType = 'INSERT' | 'UPDATE' | 'DELETE';

// リアルタイム更新のペイロード
export interface RealtimePayload<T = any> {
  eventType: RealtimeEventType;
  new: T | null;
  old: T | null;
  table: string;
  timestamp: string;
}

// コールバック関数の型定義
export type RealtimeCallback<T = any> = (payload: RealtimePayload<T>) => void;

// Supabaseリアルタイム購読管理Hook
export function useSupabaseRealtime() {
  const supabase = getSupabase();
  const subscriptionsRef = useRef<Map<string, any>>(new Map());

  // 顧客データのリアルタイム更新を購読
  const subscribeToCustomers = useCallback((callback: RealtimeCallback<Customer>) => {
    if (!supabase) {
      logger.warn('⚠️ Supabase not available for realtime subscription');
      return null;
    }

    logger.log('📡 Setting up customers realtime subscription...');

    const subscription = supabase
      .channel('customers-changes')
      .on(
        'postgres_changes',
        {
          event: '*', // INSERT, UPDATE, DELETE
          schema: 'public',
          table: 'customers'
        },
        (payload) => {
          logger.log('🔄 Customer realtime update:', payload);
          
          const realtimePayload: RealtimePayload<Customer> = {
            eventType: payload.eventType as RealtimeEventType,
            new: payload.new as Customer | null,
            old: payload.old as Customer | null,
            table: 'customers',
            timestamp: new Date().toISOString()
          };

          callback(realtimePayload);
        }
      )
      .subscribe((status) => {
        logger.log('📡 Customers subscription status:', status);
      });

    const subscriptionId = 'customers';
    subscriptionsRef.current.set(subscriptionId, subscription);

    return subscriptionId;
  }, [supabase]);

  // 予約データのリアルタイム更新を購読
  const subscribeToReservations = useCallback((callback: RealtimeCallback<ExtendedReservation>) => {
    if (!supabase) {
      logger.warn('⚠️ Supabase not available for realtime subscription');
      return null;
    }

    logger.log('📡 Setting up reservations realtime subscription...');

    const subscription = supabase
      .channel('reservations-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'reservations'
        },
        (payload) => {
          logger.log('🔄 Reservation realtime update:', payload);
          
          const realtimePayload: RealtimePayload<ExtendedReservation> = {
            eventType: payload.eventType as RealtimeEventType,
            new: payload.new as ExtendedReservation | null,
            old: payload.old as ExtendedReservation | null,
            table: 'reservations',
            timestamp: new Date().toISOString()
          };

          callback(realtimePayload);
        }
      )
      .subscribe((status) => {
        logger.log('📡 Reservations subscription status:', status);
      });

    const subscriptionId = 'reservations';
    subscriptionsRef.current.set(subscriptionId, subscription);

    return subscriptionId;
  }, [supabase]);

  // 施術データのリアルタイム更新を購読
  const subscribeToTreatments = useCallback((callback: RealtimeCallback) => {
    if (!supabase) {
      logger.warn('⚠️ Supabase not available for realtime subscription');
      return null;
    }

    logger.log('📡 Setting up treatments realtime subscription...');

    const subscription = supabase
      .channel('treatments-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'treatments'
        },
        (payload) => {
          logger.log('🔄 Treatment realtime update:', payload);
          
          const realtimePayload: RealtimePayload = {
            eventType: payload.eventType as RealtimeEventType,
            new: payload.new,
            old: payload.old,
            table: 'treatments',
            timestamp: new Date().toISOString()
          };

          callback(realtimePayload);
        }
      )
      .subscribe((status) => {
        logger.log('📡 Treatments subscription status:', status);
      });

    const subscriptionId = 'treatments';
    subscriptionsRef.current.set(subscriptionId, subscription);

    return subscriptionId;
  }, [supabase]);

  // 特定の購読を解除
  const unsubscribe = useCallback((subscriptionId: string) => {
    const subscription = subscriptionsRef.current.get(subscriptionId);
    if (subscription) {
      logger.log(`📡 Unsubscribing from ${subscriptionId}...`);
      supabase?.removeChannel(subscription);
      subscriptionsRef.current.delete(subscriptionId);
    }
  }, [supabase]);

  // 全ての購読を解除
  const unsubscribeAll = useCallback(() => {
    logger.log('📡 Unsubscribing from all realtime channels...');
    subscriptionsRef.current.forEach((subscription, id) => {
      logger.log(`📡 Removing channel: ${id}`);
      supabase?.removeChannel(subscription);
    });
    subscriptionsRef.current.clear();
  }, [supabase]);

  // コンポーネントアンマウント時の自動清掃
  useEffect(() => {
    return () => {
      unsubscribeAll();
    };
  }, [unsubscribeAll]);

  // リアルタイム接続状態チェック
  const checkRealtimeStatus = useCallback(() => {
    if (!supabase) {
      return { available: false, subscriptions: 0 };
    }

    return {
      available: true,
      subscriptions: subscriptionsRef.current.size,
      activeChannels: Array.from(subscriptionsRef.current.keys())
    };
  }, [supabase]);

  return {
    // 購読メソッド
    subscribeToCustomers,
    subscribeToReservations,
    subscribeToTreatments,
    
    // 管理メソッド
    unsubscribe,
    unsubscribeAll,
    checkRealtimeStatus,
    
    // 状態
    isSupabaseAvailable: !!supabase
  };
}

// 顧客管理専用のリアルタイムHook
export function useCustomersRealtime(
  onCustomerChange?: (customers: Customer[]) => void
) {
  const { subscribeToCustomers, unsubscribe } = useSupabaseRealtime();

  useEffect(() => {
    if (!onCustomerChange) return;

    const subscriptionId = subscribeToCustomers((payload) => {
      // 実際の顧客データ更新ロジックは、親コンポーネントで実装
      logger.log('🔄 Customer change detected:', payload.eventType);
      
      // プラットフォーム通知（オプション）
      if (payload.eventType === 'INSERT' && payload.new) {
        logger.log(`✅ New customer added: ${payload.new.name}`);
      } else if (payload.eventType === 'UPDATE' && payload.new) {
        logger.log(`📝 Customer updated: ${payload.new.name}`);
      } else if (payload.eventType === 'DELETE' && payload.old) {
        logger.log(`🗑️ Customer deleted: ${payload.old.name}`);
      }
    });

    return () => {
      if (subscriptionId) {
        unsubscribe(subscriptionId);
      }
    };
  }, [subscribeToCustomers, unsubscribe, onCustomerChange]);
}

// Issue #237: リアルタイム更新の使用例
export const REALTIME_USAGE_EXAMPLE = `
// Customers.tsx内での使用例
import { useCustomersRealtime } from '../hooks/useSupabaseRealtime';

const Customers: React.FC = () => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  
  // リアルタイム更新の設定
  useCustomersRealtime(() => {
    // 顧客データが変更された時に再取得
    const reloadCustomers = async () => {
      // DataServiceを動的importして使用
      const { DataService } = await import('../services/dataService');
      const updatedCustomers = await DataService.getCustomers();
      setCustomers(updatedCustomers);
    };
    reloadCustomers();
  });

  // 他のコンポーネントロジック...
}
`;